import { PaypalStates, ErrorTypes } from './checkout-states'

// PayPal callbacks handed to FramePay, which forwards them to `paypal.Buttons({...})`.
// FramePay re-renders the button on every `Rebilly.update()` (any order-total change),
// replaying these callbacks. The approval is a vaulted billing agreement, not bound to
// an amount, so it stays valid — the guards below keep a replay from downgrading it.
export type PaypalCallbacks = {
  onInit: () => void
  onApprove: (data?: unknown, actions?: unknown) => void
  onError: (data?: unknown, actions?: unknown) => void
  onCancel: (data?: unknown, actions?: unknown) => void
  onClick: (data?: unknown, actions?: unknown) => void
}

// Gated on the same predicate the submit gate uses (approved AND a minted token), so
// the two cannot diverge: a tokenless approval — possible when the buyer switches
// payment type mid-popup, which files the token under the other method — must stay
// recoverable instead of being held in a state submit would reject.
const paypalApproved = (): boolean =>
  globalThis.Checkout.store.payment.paypal.state.get()?.state == PaypalStates.PAYMENT_METHOD_APPROVED &&
  !!globalThis.Checkout.store.payment.paypal.token.get()

// The ERROR state auto-clears back to INITIALIZED on a delay (see the paypal state
// listener in checkout-machine). Guarded like the callbacks: an approval that lands
// while that delay is pending must not be downgraded.
export const resetPaypalErrorState = (): void => {
  if (paypalApproved()) return
  globalThis.Checkout.store.payment.paypal.state.set({ state: PaypalStates.INITIALIZED })
}

export const buildPaypalCallbacks = (): PaypalCallbacks => ({
  onInit: () => {
    console.log('[PayPal] - onInit called')
    if (paypalApproved()) return
    globalThis.Checkout.store.payment.paypal.state.set({ state: PaypalStates.INITIALIZED })
  },
  onApprove: (data, actions) => {
    console.log('[PayPal] - onApprove called', data, actions)
    globalThis.Checkout.store.payment.paypal.state.set({
      state: PaypalStates.PAYMENT_METHOD_APPROVED,
    })
  },
  onError: (data, actions) => {
    console.log('[PayPal] - onError called', data, actions)
    if (paypalApproved()) return

    globalThis.Checkout.store.payment.paypal.state.set({
      state: PaypalStates.ERROR,
      code: ErrorTypes.PAYPAL_CUSTOM_ERROR,
      message: 'Something unexpected happened',
    })
  },
  onCancel: (data, actions) => {
    console.log('[PayPal] - onCancel called', data, actions)
    if (paypalApproved()) return

    globalThis.Checkout.store.payment.paypal.state.set({
      state: PaypalStates.ERROR,
      code: ErrorTypes.PAYPAL_DECLINED_ERROR,
    })
  },
  onClick: (data, actions) => {
    console.log('[PayPal] - onClick called', data, actions)
    if (paypalApproved()) return
    globalThis.Checkout.store.payment.paypal.state.set({
      state: PaypalStates.ADDING_PAYMENT_METHOD,
    })
  },
})
